import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsNfps.css';
import './OpsConsumerDeletionQueue.css';

const ADDITIONAL_REASON_FILTERS = [
  'brand_matched_store_not_found',
  'store_location_unconfirmed',
  'ambiguous_store_match',
  'store_matched_account_not_enrolled',
  'marketplace_brand_channel_single_store',
  'marketplace_store_ambiguous',
  'processor_embedded_partner',
  'rejected_on_reprocess',
];

function initialOverrideState() {
  return {
    storeId: '',
    accountId: '',
    reasonCode: 'manual_match_override',
    reasonNote: '',
    queueStatus: '',
    loading: false,
    error: '',
    success: '',
  };
}

function PlaidContextCell({ row }) {
  const ctx = row?.plaidContext || {};
  const merchant = ctx.merchantName || ctx.displayName || ctx.originalDescription || '—';
  const description = ctx.displayName && ctx.displayName !== merchant ? ctx.displayName : ctx.originalDescription;
  const location = ctx.location?.label || null;
  const details = [
    ctx.paymentChannel ? `Channel: ${ctx.paymentChannel}` : null,
    ctx.locationType ? `Type: ${ctx.locationType}` : null,
    ctx.category ? `Category: ${ctx.category}` : null,
  ].filter(Boolean);
  const counterparties = Array.isArray(ctx.counterparties) ? ctx.counterparties : [];

  return (
    <div className="ops-tx-match-plaid-cell">
      <div><strong>{merchant}</strong></div>
      {description && description !== merchant ? (
        <div style={{ fontSize: 12, opacity: 0.82 }}>Desc: {description}</div>
      ) : null}
      {ctx.originalDescription && ctx.originalDescription !== description && ctx.originalDescription !== merchant ? (
        <div style={{ fontSize: 12, opacity: 0.82 }}>Original: {ctx.originalDescription}</div>
      ) : null}
      {location ? (
        <div style={{ fontSize: 12, opacity: 0.82 }}>Location: {location}</div>
      ) : null}
      {ctx.location?.storeNumber ? (
        <div style={{ fontSize: 12, opacity: 0.82 }}>Store #: {ctx.location.storeNumber}</div>
      ) : null}
      {details.length ? (
        <div style={{ fontSize: 12, opacity: 0.82 }}>{details.join(' | ')}</div>
      ) : null}
      {ctx.merchantEntityId ? (
        <div style={{ fontSize: 12, opacity: 0.82 }}>Entity: {ctx.merchantEntityId}</div>
      ) : null}
      {counterparties.length ? (
        <div style={{ fontSize: 12, opacity: 0.82 }}>
          Counterparty: {counterparties.map((party) => [party.name, party.type].filter(Boolean).join(' / ')).filter(Boolean).join(', ')}
        </div>
      ) : null}
    </div>
  );
}

export default function OpsTransactionMatchQueue() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [reasonFilter, setReasonFilter] = useState('');
  const [queueStatusFilter, setQueueStatusFilter] = useState('');
  const [states, setStates] = useState({});
  const [reprocessRejections, setReprocessRejections] = useState({ totalReturned: 0, clustered: false, groups: [] });
  const [activeView, setActiveView] = useState(
    searchParams.get('clearinghouse') === 'rejected_on_reprocess' ? 'reprocess_rejections' : 'match_queue'
  );

  async function loadQueue() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('days', String(days));
      params.set('limit', '100');
      params.set('page', '1');
      if (reasonFilter) params.set('unmatchedReason', reasonFilter);
      if (queueStatusFilter) params.set('queueStatus', queueStatusFilter);
      const data = await apiFetch(`/ops/program-health/transaction-match-queue?${params.toString()}`);
      const items = Array.isArray(data?.items) ? data.items : [];
      setRows(items);
      setReasons(Array.isArray(data?.reasons) ? data.reasons : []);
      setStates((prev) => {
        const next = {};
        for (const row of items) {
          const current = prev[row.transactionId] || initialOverrideState();
          next[row.transactionId] = {
            ...initialOverrideState(),
            ...current,
            storeId: current.storeId || row.matchOverride?.storeId || row.matchedStoreId || '',
            accountId: current.accountId || row.matchOverride?.accountId || row.defaultAccountId || '',
            reasonCode: current.reasonCode || row.matchOverride?.reasonCode || 'manual_match_override',
            reasonNote: current.reasonNote || '',
            queueStatus: current.queueStatus || row.queueStatus || '',
          };
        }
        return next;
      });
    } catch (err) {
      const status = err?.status;
      if (status === 401 || status === 403) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return;
      }
      setError(err?.data?.error || err?.message || 'Failed to load transaction match queue');
      setRows([]);
      setReasons([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadReprocessRejections() {
    try {
      const data = await apiFetch('/ops/clearinghouse/reprocess-rejections?limit=25');
      setReprocessRejections({
        totalReturned: Number(data?.totalReturned || 0),
        clustered: data?.clustered === true,
        groups: Array.isArray(data?.groups) ? data.groups : [],
      });
    } catch (_err) {
      setReprocessRejections({ totalReturned: 0, clustered: false, groups: [] });
    }
  }

  async function refreshAll() {
    await Promise.all([loadQueue(), loadReprocessRejections()]);
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, reasonFilter, queueStatusFilter]);

  useEffect(() => {
    if (searchParams.get('clearinghouse') === 'rejected_on_reprocess') {
      setActiveView('reprocess_rejections');
    }
  }, [searchParams]);

  function updateState(transactionId, patch) {
    setStates((prev) => ({
      ...prev,
      [transactionId]: { ...(prev[transactionId] || initialOverrideState()), ...patch },
    }));
  }

  async function applyOverride(row) {
    const st = states[row.transactionId] || initialOverrideState();
    if (!st.storeId || !st.accountId || !st.reasonCode || !st.reasonNote) {
      updateState(row.transactionId, { error: 'storeId, accountId, reasonCode, reasonNote are required', success: '' });
      return;
    }
    updateState(row.transactionId, { loading: true, error: '', success: '' });
    try {
      await apiFetch(`/ops/transactions/${encodeURIComponent(row.transactionId)}/match-override`, {
        method: 'PUT',
        body: {
          brandId: row.matchedBrandId,
          storeId: st.storeId,
          accountId: st.accountId,
          reasonCode: st.reasonCode,
          reasonNote: st.reasonNote,
          reprocess: true,
        },
      });
      updateState(row.transactionId, {
        loading: false,
        success: 'Override applied and reprocessed',
        error: '',
      });
      await loadQueue();
    } catch (err) {
      updateState(row.transactionId, {
        loading: false,
        error: err?.data?.error || err?.message || 'Failed to apply override',
        success: '',
      });
    }
  }

  async function clearOverride(row) {
    const st = states[row.transactionId] || initialOverrideState();
    if (!row?.matchOverride?.enabled) return;
    updateState(row.transactionId, { loading: true, error: '', success: '' });
    try {
      await apiFetch(`/ops/transactions/${encodeURIComponent(row.transactionId)}/match-override`, {
        method: 'PUT',
        body: {
          clearOverride: true,
          reasonCode: st.reasonCode || 'manual_match_override',
          reasonNote: st.reasonNote || 'Cleared manual override from Ops queue',
        },
      });
      updateState(row.transactionId, {
        loading: false,
        success: 'Override cleared',
        error: '',
      });
      await loadQueue();
    } catch (err) {
      updateState(row.transactionId, {
        loading: false,
        error: err?.data?.error || err?.message || 'Failed to clear override',
        success: '',
      });
    }
  }

  async function updateQueueStatus(row, nextStatus) {
    const st = states[row.transactionId] || initialOverrideState();
    updateState(row.transactionId, { loading: true, error: '', success: '' });
    try {
      await apiFetch(`/ops/transactions/${encodeURIComponent(row.transactionId)}/queue-status`, {
        method: 'PUT',
        body: {
          queueStatus: nextStatus,
          reasonCode: st.reasonCode || 'ops_queue_status_update',
          reasonNote: st.reasonNote || `Set queue status to ${nextStatus}`,
        },
      });
      updateState(row.transactionId, {
        loading: false,
        success: `Queue status set to ${nextStatus}`,
        error: '',
        queueStatus: nextStatus,
      });
      await loadQueue();
    } catch (err) {
      updateState(row.transactionId, {
        loading: false,
        error: err?.data?.error || err?.message || 'Failed to update queue status',
        success: '',
      });
    }
  }

  const mergedReasons = [
    ...reasons.map((r) => ({ reason: r.reason, count: Number(r.count || 0) })),
    ...ADDITIONAL_REASON_FILTERS
      .filter((reason) => !reasons.some((r) => r.reason === reason))
      .map((reason) => ({ reason, count: 0 })),
  ];
  const totalRejectedReprocess = reprocessRejections.groups.reduce((sum, group) => sum + Number(group.count || 0), 0);

  function selectView(view) {
    setActiveView(view);
    const next = new URLSearchParams(searchParams);
    if (view === 'reprocess_rejections') {
      next.set('clearinghouse', 'rejected_on_reprocess');
    } else {
      next.delete('clearinghouse');
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <OpsLayout title="Transaction Match Queue" subtitle="Brand-matched transactions missing store/account allocation">
      <div className="ops-nfp-content">
        <section className="ops-nfp-panel" style={{ marginTop: 0 }}>
          <h2 className="ops-support-queue-title">Transaction Queues</h2>
          <div className="ops-support-queue-grid" aria-label="Transaction queue views" style={{ marginBottom: 14 }}>
            <button
              type="button"
              className={`ops-support-queue-card ${activeView === 'match_queue' ? 'active' : ''}`}
              onClick={() => selectView('match_queue')}
            >
              <div className="ops-support-queue-label">Manual Match Queue</div>
              <div className="ops-support-queue-value">{Number(rows.length || 0).toLocaleString('en-US')}</div>
              <div className="ops-support-queue-caption">Transactions needing store or account allocation</div>
              <div className="ops-support-queue-description">Use this queue for manual override, queue status changes, and not-matchable decisions.</div>
            </button>
            <button
              type="button"
              className={`ops-support-queue-card ${activeView === 'reprocess_rejections' ? 'active' : ''}`}
              onClick={() => selectView('reprocess_rejections')}
            >
              <div className="ops-support-queue-label">Rejected on Reprocess</div>
              <div className="ops-support-queue-value">{Number(totalRejectedReprocess || 0).toLocaleString('en-US')}</div>
              <div className="ops-support-queue-caption">
                {reprocessRejections.clustered ? 'Repeated rejection pattern found' : 'No repeated rejection pattern'}
              </div>
              <div className="ops-support-queue-description">Transactions that looked eligible before, but did not qualify when checked again. Repeated patterns may need Ops or engineering review.</div>
            </button>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="ops-nfp-field" style={{ minWidth: 180 }}>
              <span style={{ fontWeight: 600 }}>Window (days)</span>
              <select value={days} onChange={(e) => setDays(Number(e.target.value) || 30)}>
                <option value={7}>7</option>
                <option value={30}>30</option>
                <option value={90}>90</option>
              </select>
            </label>
            {activeView === 'match_queue' ? (
              <>
                <label className="ops-nfp-field" style={{ minWidth: 260 }}>
                  <span style={{ fontWeight: 600 }}>Reason Filter</span>
                  <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}>
                    <option value="">All reasons</option>
                    {mergedReasons.map((r) => (
                      <option key={r.reason} value={r.reason}>
                        {`${r.reason} (${Number(r.count || 0)})`}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="ops-nfp-field" style={{ minWidth: 230 }}>
                  <span style={{ fontWeight: 600 }}>Queue Status</span>
                  <select value={queueStatusFilter} onChange={(e) => setQueueStatusFilter(e.target.value)}>
                    <option value="">All statuses</option>
                    <option value="needs_review">needs_review</option>
                    <option value="assigned_by_ops">assigned_by_ops</option>
                    <option value="not_matchable">not_matchable</option>
                    <option value="suppressed">suppressed</option>
                    <option value="reopened">reopened</option>
                  </select>
                </label>
              </>
            ) : null}
            <button type="button" className="ops-btn primary" onClick={refreshAll}>Refresh</button>
          </div>
        </section>

        {activeView === 'reprocess_rejections' ? (
        <section className="ops-nfp-panel">
          <div className="ops-nfp-panel-header">
            <h3 className="ops-section-title">Rejected on Reprocess</h3>
            <span className={`ops-chip ${reprocessRejections.clustered ? 'bad' : 'good'}`}>
              {reprocessRejections.clustered ? 'Repeated rejection pattern found' : 'No repeated rejection pattern'}
            </span>
          </div>
          <div className="ops-nfp-table-wrap">
            <table className="ops-nfp-table">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Count</th>
                  <th>Failure Reason</th>
                  <th>Platform</th>
                  <th>Processor</th>
                  <th>Matcher</th>
                  <th>Newest</th>
                  <th>Suggested Action</th>
                </tr>
              </thead>
              <tbody>
                {reprocessRejections.groups.length === 0 ? (
                  <tr><td colSpan={8}>No rejected_on_reprocess groups found.</td></tr>
                ) : reprocessRejections.groups.map((group, idx) => (
                  <tr key={`${group.brandId || 'unknown'}:${group.failureReason || 'na'}:${idx}`}>
                    <td>{group.brandName || group.brandId || '—'}</td>
                    <td>{Number(group.count || 0).toLocaleString('en-US')}</td>
                    <td>{group.failureReason || '—'}</td>
                    <td>{group.platform || '—'}</td>
                    <td>{group.processor || '—'}</td>
                    <td>{group.matcherVersion || '—'}</td>
                    <td>{group.newestTransactionDate ? new Date(group.newestTransactionDate).toLocaleString() : '—'}</td>
                    <td>{group.suggestedAction || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        ) : null}

        {activeView === 'match_queue' ? (
        <section className="ops-nfp-panel">
          <h3 className="ops-section-title">Queue</h3>
          {loading && <div className="ops-message">Loading queue…</div>}
          {!loading && error && <div className="ops-message error">{error}</div>}
          {!loading && !error && (
            <div className="ops-nfp-table-wrap">
              <table className="ops-nfp-table ops-tx-match-table">
                <thead>
                  <tr>
                    <th>Txn</th>
                    <th>Brand</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Plaid Data</th>
                    <th>Match</th>
                    <th>Queue Status</th>
                    <th>Store</th>
                    <th>Account ID</th>
                    <th>Reason</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={11}>No unmatched transactions in this window.</td></tr>
                  )}
                  {rows.map((row) => {
                    const st = states[row.transactionId] || initialOverrideState();
                    return (
                      <tr key={row.transactionId}>
                        <td>
                          <div>{row.plaidTransactionId || row.transactionId}</div>
                          <div style={{ fontSize: 12, opacity: 0.8 }}>{row.transactionId}</div>
                        </td>
                        <td>{row.matchedBrandName || row.matchedBrandId || '—'}</td>
                        <td>{row.transactionDate ? new Date(row.transactionDate).toLocaleString() : '—'}</td>
                        <td>{(Number(row.amountCents || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                        <td><PlaidContextCell row={row} /></td>
                        <td>
                          <div>{row.matchMethod || '—'}</div>
                          <div style={{ fontSize: 12, opacity: 0.8 }}>{row.matchConfidence || '—'}{Number.isFinite(row.matchScore) ? ` • score ${row.matchScore}` : ''}</div>
                          {Array.isArray(row.matchReasons) && row.matchReasons.length ? (
                            <div style={{ fontSize: 12, opacity: 0.8 }}>{row.matchReasons.join(', ')}</div>
                          ) : null}
                          {Number.isFinite(row.runnerUpScore) ? (
                            <div style={{ fontSize: 12, opacity: 0.8 }}>
                              runner-up: {row.runnerUpStoreId || '—'} ({row.runnerUpScore})
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <div>
                            <strong>{row.queueStatus || '—'}</strong>
                            {row.requiresOpsReview ? ' • review' : ''}
                          </div>
                          {row.queueStatusReasonCode ? (
                            <div style={{ fontSize: 12, opacity: 0.8 }}>{row.queueStatusReasonCode}</div>
                          ) : null}
                          {row.queueStatusReasonNote ? (
                            <div style={{ fontSize: 12, opacity: 0.8 }}>{row.queueStatusReasonNote}</div>
                          ) : null}
                        </td>
                        <td>
                          <select
                            value={st.storeId}
                            onChange={(e) => updateState(row.transactionId, { storeId: e.target.value, error: '', success: '' })}
                          >
                            <option value="">Select store</option>
                            {(Array.isArray(row.candidateStores) ? row.candidateStores : []).map((store) => (
                              <option key={store.storeId} value={store.storeId}>
                                {store.label || `${store.name || 'Store'}${store.address ? ` - ${store.address}` : ''}`}
                              </option>
                            ))}
                          </select>
                          {st.storeId && (Array.isArray(row.candidateStores) ? row.candidateStores : []).some((store) => store.storeId === st.storeId) ? (
                            <div className="ops-tx-match-store-detail" style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
                              {(() => {
                                const selectedStore = (row.candidateStores || []).find((store) => store.storeId === st.storeId);
                                const details = [
                                  selectedStore?.locationLabel || selectedStore?.address || null,
                                  selectedStore?.status ? `Status: ${selectedStore.status}${selectedStore.isActive === false ? ' (inactive)' : ''}` : null,
                                ].filter(Boolean);
                                return details.length ? details.join(' | ') : '—';
                              })()}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <input
                            value={st.accountId}
                            onChange={(e) => updateState(row.transactionId, { accountId: e.target.value.trim(), error: '', success: '' })}
                            placeholder="Account ObjectId"
                          />
                        </td>
                        <td>
                          <select
                            value={st.reasonCode}
                            onChange={(e) => updateState(row.transactionId, { reasonCode: e.target.value, error: '', success: '' })}
                          >
                            <option value="manual_match_override">manual_match_override</option>
                            <option value="store_location_correction">store_location_correction</option>
                            <option value="account_attribution_correction">account_attribution_correction</option>
                            <option value="other">other</option>
                          </select>
                          <textarea
                            className="ops-tx-reason-note"
                            rows={4}
                            value={st.reasonNote}
                            onChange={(e) => updateState(row.transactionId, { reasonNote: e.target.value, error: '', success: '' })}
                            placeholder="Reason note for the audit trail"
                          />
                          <select
                            value={st.queueStatus || row.queueStatus || ''}
                            onChange={(e) => updateState(row.transactionId, { queueStatus: e.target.value, error: '', success: '' })}
                            style={{ marginTop: 6 }}
                          >
                            <option value="">Select queue status</option>
                            <option value="needs_review">needs_review</option>
                            <option value="assigned_by_ops">assigned_by_ops</option>
                            <option value="not_matchable">not_matchable</option>
                            <option value="suppressed">suppressed</option>
                            <option value="reopened">reopened</option>
                          </select>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="ops-btn primary"
                            disabled={st.loading}
                            onClick={() => applyOverride(row)}
                          >
                            {st.loading ? 'Applying…' : 'Apply'}
                          </button>
                          <button
                            type="button"
                            className="ops-btn"
                            style={{ marginTop: 6 }}
                            disabled={st.loading || !row?.matchOverride?.enabled}
                            onClick={() => clearOverride(row)}
                            title={row?.matchOverride?.enabled ? 'Clear the current manual override.' : 'No manual override is currently set.'}
                          >
                            Clear Override
                          </button>
                          <button
                            type="button"
                            className="ops-btn"
                            style={{ marginTop: 6 }}
                            disabled={st.loading || !(st.queueStatus || row.queueStatus)}
                            onClick={() => updateQueueStatus(row, st.queueStatus || row.queueStatus)}
                          >
                            Save Status
                          </button>
                          <button
                            type="button"
                            className="ops-btn"
                            style={{ marginTop: 6 }}
                            disabled={st.loading}
                            onClick={() => updateQueueStatus(row, 'not_matchable')}
                          >
                            Mark Not Matchable
                          </button>
                          <button
                            type="button"
                            className="ops-btn"
                            style={{ marginTop: 6 }}
                            disabled={st.loading}
                            onClick={() => updateQueueStatus(row, 'reopened')}
                          >
                            Reopen
                          </button>
                          {row?.matchOverride?.enabled ? (
                            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
                              Override active
                            </div>
                          ) : null}
                          {st.error ? <div className="ops-message error" style={{ marginTop: 6 }}>{st.error}</div> : null}
                          {st.success ? <div className="ops-message" style={{ marginTop: 6 }}>{st.success}</div> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        ) : null}
      </div>
    </OpsLayout>
  );
}
